import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useCompleteCheckoutMutation,
  useGetSubscriptionStatusQuery,
  useOpenBillingPortalMutation,
  useStartCheckoutMutation,
} from "../../slices/SubscriptionSlice";
import { SUBSCRIPTION_REQUIRED_EVENT } from "./subscription.utils";

export const currentOrgId = (): string => getLinkedFirm()?.orgId || "";

/**
 * The firm's seat-billing status (cheap, database only). Checked again every
 * 15 minutes and whenever an API call answers "subscription_required".
 */
export const useSubscriptionStatus = (orgIdArg?: string) => {
  const orgId = orgIdArg ?? currentOrgId();
  const query = useGetSubscriptionStatusQuery(
    { orgId },
    { skip: !orgId, pollingInterval: 15 * 60 * 1000 },
  );
  const { refetch, isUninitialized } = query;
  useEffect(() => {
    const onRequired = () => {
      if (!isUninitialized) refetch();
    };
    window.addEventListener(SUBSCRIPTION_REQUIRED_EVENT, onRequired);
    return () =>
      window.removeEventListener(SUBSCRIPTION_REQUIRED_EVENT, onRequired);
  }, [refetch, isUninitialized]);
  return { ...query, orgId, status: query.data };
};

/** "Add payment method" (Stripe Checkout) and "Change" (Stripe Billing Portal). */
export const useBillingRedirects = (orgId: string) => {
  const [startCheckout, checkoutState] = useStartCheckoutMutation();
  const [openPortal, portalState] = useOpenBillingPortalMutation();
  const [error, setError] = useState("");

  const go = useCallback(
    async (kind: "checkout" | "portal") => {
      setError("");
      try {
        const { url } =
          kind === "checkout"
            ? await startCheckout({ orgId }).unwrap()
            : await openPortal({ orgId }).unwrap();
        if (url) window.location.assign(url);
      } catch (e) {
        setError(
          typeof e === "string" ? e : "Something went wrong. Please try again.",
        );
      }
    },
    [orgId, startCheckout, openPortal],
  );

  return {
    addPaymentMethod: () => go("checkout"),
    openPortal: () => go("portal"),
    busy: checkoutState.isLoading || portalState.isLoading,
    error,
  };
};

/**
 * Back from Stripe Checkout (?setup=done&session_id=cs_...): finish at once
 * (the webhook does the same, both are safe twice), then clean the address.
 * Runs wherever the app lands, also on the "Add a payment method" page.
 */
export const useCheckoutReturn = (orgId: string, onDone?: () => void) => {
  const [complete] = useCompleteCheckoutMutation();
  const started = useRef(false);
  useEffect(() => {
    if (!orgId || started.current) return;
    const params = new URLSearchParams(window.location.search);
    const setup = params.get("setup");
    if (!setup) return;
    started.current = true;
    const sessionId = params.get("session_id") || "";
    const clean = () => {
      params.delete("setup");
      params.delete("session_id");
      const query = params.toString();
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + (query ? `?${query}` : ""),
      );
    };
    if (setup !== "done" || !sessionId.startsWith("cs_")) {
      clean();
      return;
    }
    complete({ orgId, sessionId })
      .unwrap()
      .then((result) => {
        if (result.done) toast.success("Payment method added. Thank you!");
        else
          toast.info(
            "Stripe is still confirming your card. This page updates in a moment.",
          );
      })
      .catch((e) =>
        toast.error(
          typeof e === "string"
            ? e
            : "Your card couldn't be confirmed. Please try again.",
        ),
      )
      .finally(() => {
        clean();
        onDone?.();
      });
  }, [orgId, complete, onDone]);
};
